// apps/client/src/pages/reports/SuccessionReadinessReportPage.tsx
// SUCCESSION READINESS REPORT - 15-item buyer due diligence checklist
// Based on Ash Ross (LMG) succession readiness framework

import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Share2, X, Copy, Check, Loader2, Shield, TrendingUp,
  Users, Database, Building2, Clock, BarChart3, Lock,
  CheckCircle2, AlertCircle, Vault,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import StatCard from '../../components/common/StatCard';
import { PageHeader } from '../../components/common/PageHeader';
import { useSuccessionReadiness, ChecklistItem } from '../../hooks/useSuccessionReadiness';
import { formatCurrencyShorthand } from '../../utils/formatters';

// Share Modal Component (reused from TrailBookValuationPage pattern)
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, shareUrl, isLoading, error }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-white">Share Succession Readiness Report</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            <span className="ml-3 text-gray-300">Generating share link...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg mb-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {shareUrl && !isLoading && (
          <>
            <p className="text-gray-300 mb-4">
              Share this link to give read-only access to this succession readiness report.
              The link includes a snapshot of the current data.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-sm text-gray-500">
              This link expires in 30 days.
            </p>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Readiness Score Ring
const ReadinessScoreRing: React.FC<{ score: number }> = ({ score }) => {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const getScoreColor = (s: number) => {
    if (s >= 80) return 'text-green-400';
    if (s >= 60) return 'text-blue-400';
    if (s >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStrokeColor = (s: number) => {
    if (s >= 80) return 'stroke-green-400';
    if (s >= 60) return 'stroke-blue-400';
    if (s >= 40) return 'stroke-yellow-400';
    return 'stroke-red-400';
  };

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60" cy="60" r={radius}
          fill="none" stroke="currentColor"
          className="text-gray-700" strokeWidth="8"
        />
        <circle
          cx="60" cy="60" r={radius}
          fill="none" strokeWidth="8"
          className={getStrokeColor(score)}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{score}</span>
        <span className="text-xs text-gray-400">/ 100</span>
      </div>
    </div>
  );
};

// Checklist Item Row — full-width horizontal layout
const ChecklistRow: React.FC<{ item: ChecklistItem }> = ({ item }) => {
  const isAutomated = item.status === 'automated';
  const hasData = item.value !== null;

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 rounded-lg border transition-colors ${
        isAutomated && hasData
          ? 'bg-gray-800 border-gray-700'
          : 'bg-gray-800/50 border-gray-700/50'
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="text-xs font-mono text-gray-500 w-6 shrink-0">#{item.id}</span>
        {isAutomated && hasData ? (
          <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
        ) : (
          <Lock className="w-4 h-4 text-gray-500 shrink-0" />
        )}
        <span className={`text-sm font-medium truncate ${hasData ? 'text-white' : 'text-gray-500'}`}>
          {item.label}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-4">
        {hasData ? (
          <span className="text-base font-semibold text-white">
            {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
          </span>
        ) : (
          <span className="text-sm text-gray-500 italic hidden sm:inline">Pending</span>
        )}
        {item.detail?.source === 'crm' && (
          <span className="text-[0.625rem] px-1.5 py-0.5 rounded-full whitespace-nowrap bg-blue-500/20 text-blue-400">
            CRM
          </span>
        )}
        <span className={`text-[0.625rem] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
          isAutomated
            ? 'bg-green-500/20 text-green-400'
            : 'bg-gray-600/30 text-gray-500'
        }`}>
          {isAutomated ? 'Auto' : 'Manual'}
        </span>
        <span className="text-xs text-gray-500 w-8 text-right">{item.weight}pt</span>
      </div>
    </div>
  );
};

const SuccessionReadinessReportPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<any>();

  const {
    data,
    loading,
    error,
    generatedAt,
  } = useSuccessionReadiness({
    companyId: userData?.companyId || '',
  });

  // Share modal state
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    shareUrl: string | null;
    isLoading: boolean;
    error: string | null;
  }>({
    isOpen: false,
    shareUrl: null,
    isLoading: false,
    error: null,
  });

  const handleShare = async () => {
    setShareModal({ isOpen: true, shareUrl: null, isLoading: true, error: null });

    try {
      const generateShareTokenFunc = httpsCallable<
        { companyId: string; expiresInDays?: number },
        { shareId: string; token: string; shareUrl: string }
      >(functions, 'generateSuccessionShareToken');

      const response = await generateShareTokenFunc({
        companyId: userData?.companyId,
        expiresInDays: 30,
      });

      setShareModal({
        isOpen: true,
        shareUrl: response.data.shareUrl,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Failed to generate share link:', err);
      setShareModal({
        isOpen: true,
        shareUrl: null,
        isLoading: false,
        error: err.message || 'Failed to generate share link. Please try again.',
      });
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, shareUrl: null, isLoading: false, error: null });
  };

  if (loading) {
    return <div className="p-8 text-center text-white">Loading succession readiness report...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading report: {error}
      </div>
    );
  }

  if (!data) return null;

  const automatedItems = data.checklist.filter((i) => i.status === 'automated');
  const manualItems = data.checklist.filter((i) => i.status !== 'automated');
  const completeItems = data.checklist.filter((i) => i.value !== null).length;
  const crmItem = data.checklist.find((i) => i.detail?.source === 'crm');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="Succession Readiness"
        subtitle="Buyer due diligence checklist — powered by your commission data"
        actions={
          <button
            onClick={handleShare}
            disabled={shareModal.isLoading || loading}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium border border-gray-600/50 text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {shareModal.isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Share2 className="w-3.5 h-3.5" />
            )}
            <span>Share</span>
          </button>
        }
      />

      {/* Readiness Score + Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6">
        {/* Score Ring */}
        <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center justify-center">
          <ReadinessScoreRing score={data.readinessScore} />
          <p className="mt-3 text-sm text-gray-400 text-center">
            Readiness Score
          </p>
          <p className="text-xs text-gray-500 text-center mt-1">
            {completeItems} of {data.checklist.length} items complete
          </p>
        </div>

        {/* Summary Cards */}
        <StatCard
          title="Active Clients on Trail"
          value={data.activeClientsOnTrail.toLocaleString()}
          subtitle="Line of Trail, as shown on your dashboard"
          icon={<Users size={24} />}
        />
        <StatCard
          title="Loans Under Management"
          value={data.totalLoansUnderManagement.toLocaleString()}
          subtitle="On book, not discharged"
          icon={<Vault size={24} />}
        />
        <StatCard
          title="Total Database"
          value={data.totalDatabase.toLocaleString()}
          subtitle="Every loan ever written, active + discharged"
          icon={<Database size={24} />}
        />
        <StatCard
          title="Loans Settled (2yr)"
          value={data.loansSettledLast2Years.toLocaleString()}
          subtitle="New business in last 24 months"
          icon={<TrendingUp size={24} />}
        />
      </div>

      {/* Loan book value — the number a buyer prices off */}
      {typeof data.loanBookCents === 'number' && data.loanBookCents > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <Vault className="w-4 h-4" />
            Loan Book Under Management
          </h3>
          <div className="flex flex-wrap items-baseline gap-4">
            <span className="text-3xl font-bold text-white">
              {formatCurrencyShorthand(data.loanBookCents)}
            </span>
            <span className="text-sm text-gray-400">
              across {data.totalLoansUnderManagement.toLocaleString()} loans
              {typeof data.actualTrailing12mCents === 'number' && data.actualTrailing12mCents > 0 && (
                <> · {formatCurrencyShorthand(data.actualTrailing12mCents)} trail billed in the last 12 months</>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Lender Mix */}
      {data.lenderMix.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Lender Diversification — Top {Math.min(data.lenderMix.length, 10)}
          </h3>
          <div className="space-y-2">
            {data.lenderMix.slice(0, 10).map((lender) => (
              <div key={lender.lender} className="flex items-center gap-3">
                <span className="text-sm text-gray-300 w-40 truncate">{lender.lender}</span>
                <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${Math.min(lender.percentage, 100)}%` }}
                  />
                </div>
                <span className="text-sm text-gray-400 w-20 text-right">
                  {lender.loanCount} ({lender.percentage}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loan Age Distribution */}
      {data.loanAgeDistribution.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-300 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Loan Maturity Distribution
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.loanAgeDistribution.map((bucket) => (
              <div key={bucket.bucket} className="bg-gray-900/50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-white">{bucket.count.toLocaleString()}</p>
                <p className="text-sm text-gray-400">{bucket.bucket}</p>
                <p className="text-xs text-gray-500">{bucket.percentage}%</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* YoY Growth */}
      {data.loansNowVs12MonthsAgo.growthPercent !== null && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Book Growth (12 months)
          </h3>
          <div className="flex items-baseline gap-4">
            <span className={`text-3xl font-bold ${
              data.loansNowVs12MonthsAgo.growthPercent >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {data.loansNowVs12MonthsAgo.growthPercent > 0 ? '+' : ''}
              {data.loansNowVs12MonthsAgo.growthPercent}%
            </span>
            <span className="text-sm text-gray-400">
              {data.loansNowVs12MonthsAgo.previous.toLocaleString()} loans → {data.loansNowVs12MonthsAgo.current.toLocaleString()} loans
            </span>
          </div>
        </div>
      )}

      {/* Full Checklist */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Succession Readiness Checklist
        </h3>

        {/* Automated Items */}
        <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Automated ({automatedItems.filter((i) => i.value !== null).length}/{automatedItems.length})
        </h4>
        <div className="flex flex-col gap-2 mb-6">
          {automatedItems.map((item) => (
            <ChecklistRow key={item.id} item={item} />
          ))}
        </div>

        {/* Manual Items */}
        <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Complete Your Profile ({manualItems.filter((i) => i.value !== null).length}/{manualItems.length})
        </h4>
        <div className="flex flex-col gap-2">
          {manualItems.map((item) => (
            <ChecklistRow key={item.id} item={item} />
          ))}
        </div>
      </div>

      {/* Methodology Note */}
      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">About This Report</h3>
        <p className="text-sm text-gray-400">
          This succession readiness report is based on a {data.checklist.length}-item buyer due
          diligence checklist used by industry professionals to evaluate mortgage broker businesses
          for acquisition. {automatedItems.length} items are automatically calculated from your
          commission data. The remaining {manualItems.length} require additional information to
          complete your profile and maximise your readiness score. Share this report with potential
          buyers to demonstrate the quality and transparency of your book.
        </p>
        <p className="text-sm text-gray-400 mt-2">
          Loan counts are measured per loan, and every figure reconciles to your own dashboard.
          Line of Trail comes from the same monthly snapshot the dashboard reads.
          {data.latestCompleteMonth && (
            <> Month-based figures use {data.latestCompleteMonth}, the most recent complete
            commission period.</>
          )}
        </p>
        {crmItem?.detail?.source === 'crm' && (
          <p className="text-sm text-gray-400 mt-2">
            Items marked <span className="text-blue-400">CRM</span> come from your connected CRM
            rather than your commission files, so they are measured over a different population.
            The Residential and Commercial split covers{' '}
            {crmItem.detail.typedDeals?.toLocaleString()} of{' '}
            {crmItem.detail.settledDeals?.toLocaleString()} settled CRM deals
            ({crmItem.detail.coveragePercent}%); the remainder carry no deal type and are excluded
            from the percentages rather than counted as either.
          </p>
        )}
        {generatedAt && (
          <p className="text-xs text-gray-500 mt-2">
            Report generated: {new Date(generatedAt).toLocaleString('en-AU')}
          </p>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        shareUrl={shareModal.shareUrl}
        isLoading={shareModal.isLoading}
        error={shareModal.error}
      />
    </div>
  );
};

export default SuccessionReadinessReportPage;
